import { Menu as ChakraMenu, Portal } from "@chakra-ui/react";
import type { ComponentProps, ReactNode, CSSProperties } from "react";
import React, { forwardRef, useState } from "react";
import SanitizedChildren from "@ui-kit/other/SanitizedChildren/SanitizedChildren.web";
import Box from "@ui-kit/layout/Box/Box.web";
import type { ButtonSharedProps } from "../../form/Button/Button.props";
import type { MenuSharedProps } from "./Menu.props";

type ChakraMenuRootProps = ComponentProps<typeof ChakraMenu.Root>;
type ChakraMenuTriggerProps = ComponentProps<typeof ChakraMenu.Trigger>;
type ChakraMenuContentProps = ComponentProps<typeof ChakraMenu.Content>;
type ChakraMenuItemProps = ComponentProps<typeof ChakraMenu.Item>;
type ChakraMenuSeparatorProps = ComponentProps<typeof ChakraMenu.Separator>;

// v1/v2 render props context type
type MenuRenderPropsContext = {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
};

// v1/v2 placement type
type Placement =
  | "top"
  | "top-start"
  | "top-end"
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "left"
  | "left-start"
  | "left-end"
  | "right"
  | "right-start"
  | "right-end";

export type MenuProps = MenuSharedProps &
  Omit<ChakraMenuRootProps, "children" | "open" | "defaultOpen"> & {
    // v1/v2 aliases
    isOpen?: boolean;
    open?: boolean;
    closeOnBlur?: boolean;
    closeOnInteractOutside?: boolean;
    closeOnSelect?: boolean;
    isLazy?: boolean;
    lazy?: boolean;
    /** v1/v2 placement prop - maps to positioning.placement in v3 */
    placement?: Placement;
    /** v1/v2 gutter prop - distance from trigger in pixels */
    gutter?: number;

    // v3 aliases
    defaultOpen?: boolean;

    // Support v1/v2 render props pattern: children can be function or ReactNode
    children?: ReactNode | ((context: MenuRenderPropsContext) => ReactNode);
  };

export type MenuButtonProps = ChakraMenuTriggerProps &
  ButtonSharedProps & {
    children?: ReactNode;
    asChild?: boolean;
  };

export type MenuListProps = ChakraMenuContentProps & {
  children?: ReactNode;
  maxHeight?: string;
  overflowY?: "scroll" | "auto" | "hidden" | "visible";
};

/** Chakra style object type for pseudo-selectors like _hover, _focus, etc. */
interface StyleObject {
  [key: string]: string | number | StyleObject | undefined;
}

export type MenuItemProps = Omit<
  ChakraMenuItemProps,
  "value" | "children" | "onSelect"
> & {
  value: string;
  children?: ReactNode;
  onSelect?: (event: React.MouseEvent) => void;
  /** Style props for disabled state simulation */
  opacity?: number;
  cursor?: string;
  pointerEvents?: "auto" | "none";
  /** Override hover styles */
  _hover?: StyleObject;
  isDisabled?: boolean;
  disabled?: boolean;
};

export type MenuDividerProps = ChakraMenuSeparatorProps;

// Chakra v3 types are incomplete - these wrappers add missing style props
type PositionerProps = {
  style?: CSSProperties;
  children?: ReactNode;
} & Record<string, unknown>;

type ContentProps = {
  children?: ReactNode;
} & Record<string, unknown>;

type ItemProps = {
  children?: ReactNode;
} & Record<string, unknown>;

const DEFAULT_HOVER = {
  _light: { bg: "gray.100" },
  _dark: { bg: "whiteAlpha.100" },
};

const MenuButton = forwardRef<HTMLButtonElement, MenuButtonProps>(
  function MenuButton({ children, ...props }, ref) {
    const Trigger = ChakraMenu.Trigger as React.ComponentType<
      ChakraMenuTriggerProps & {
        cursor?: string;
        children?: ReactNode;
        asChild?: boolean;
      }
    >;
    return (
      <Trigger ref={ref} cursor="pointer" {...props}>
        {children}
      </Trigger>
    );
  }
);
MenuButton.displayName = "Menu.Button";

function MenuList({ children, ...props }: MenuListProps): JSX.Element {
  const Positioner =
    ChakraMenu.Positioner as React.ComponentType<PositionerProps>;
  const Content = ChakraMenu.Content as React.ComponentType<ContentProps>;

  return (
    <Portal>
      <Positioner style={{ zIndex: 99999999 }}>
        <Content
          border="none"
          borderRadius="md"
          p="none"
          overflow="hidden"
          boxShadow="0px 4px 10px rgba(0, 0, 0, 0.25)"
          minW="200px"
          bg="bg"
          {...props}
        >
          {children}
        </Content>
      </Positioner>
    </Portal>
  );
}

function MenuItem({
  children,
  value,
  opacity,
  cursor = "pointer",
  pointerEvents,
  _hover,
  isDisabled,
  disabled,
  onSelect,
  ...rest
}: MenuItemProps): JSX.Element {
  const ChakraMenuItem = ChakraMenu.Item as React.ComponentType<ItemProps>;

  // Use onClick to trigger onSelect for better test compatibility.
  // Chakra v3's native onSelect uses Radix primitives which don't
  // fire correctly in jsdom test environments.
  const handleClick = (event: React.MouseEvent) => {
    onSelect?.(event);
  };

  return (
    <ChakraMenuItem
      value={value}
      fontSize="md"
      p="15px 20px 15px 20px"
      bg="bg"
      color="fg"
      _hover={_hover ?? DEFAULT_HOVER}
      _focus={{
        _light: { bg: "gray.100" },
        _dark: { bg: "whiteAlpha.100" },
      }}
      // Only highlight on keyboard focus, not on hover
      // This prevents all items from showing highlight when mouse enters menu
      _highlighted={{
        bg: "transparent",
      }}
      opacity={opacity}
      cursor={cursor}
      pointerEvents={pointerEvents}
      isDisabled={disabled || isDisabled}
      onClick={handleClick}
      {...rest}
    >
      <SanitizedChildren>
        {children as ReactNode | Iterable<ReactNode>}
      </SanitizedChildren>
    </ChakraMenuItem>
  );
}

function MenuLabel(props: ComponentProps<typeof Box>): JSX.Element {
  return (
    <Box py="0.4rem" px="0.8rem" fontSize="sm" userSelect="none" {...props} />
  );
}

function MenuDivider(props: MenuDividerProps): JSX.Element {
  return <ChakraMenu.Separator {...props} />;
}

interface MenuComponent {
  (props: MenuProps): JSX.Element;
  Button: typeof MenuButton;
  List: typeof MenuList;
  Item: typeof MenuItem;
  Label: typeof MenuLabel;
  Divider: typeof MenuDivider;
}

function MenuRoot(props: MenuProps): JSX.Element {
  const {
    children,
    isOpen: controlledIsOpen,
    open,
    closeOnBlur,
    closeOnInteractOutside,
    closeOnSelect,
    isLazy,
    lazy,
    defaultOpen,
    onOpenChange,
    placement,
    gutter,
    positioning: positioningProp,
    ...rest
  } = props;

  // Track internal open state for render props pattern
  const [internalOpen, setInternalOpen] = useState(defaultOpen ?? false);

  const openValue = open !== undefined ? open : controlledIsOpen;
  const isControlled = openValue !== undefined;
  const isOpen = isControlled ? openValue : internalOpen;

  const closeOnInteractOutsideValue =
    closeOnInteractOutside !== undefined ? closeOnInteractOutside : closeOnBlur;
  const lazyValue = lazy !== undefined ? lazy : isLazy;

  // Build positioning object from v1/v2 props or use v3 positioning prop
  const positioning = positioningProp ?? {
    placement: placement ?? "bottom-start",
    gutter: gutter ?? 4,
  };

  const handleOpenChange = (details: { open: boolean }) => {
    if (!isControlled) {
      setInternalOpen(details.open);
    }
    onOpenChange?.(details);
  };

  const onClose = () => handleOpenChange({ open: false });
  const onOpen = () => handleOpenChange({ open: true });

  // Support v1/v2 render props pattern
  const resolvedChildren =
    typeof children === "function"
      ? children({ isOpen, onClose, onOpen })
      : children;

  return (
    <ChakraMenu.Root
      open={isOpen}
      defaultOpen={defaultOpen}
      closeOnSelect={closeOnSelect}
      onOpenChange={handleOpenChange}
      lazyMount={lazyValue}
      unmountOnExit={lazyValue}
      positioning={positioning}
      onInteractOutside={
        closeOnInteractOutsideValue === false
          ? (event: { preventDefault: () => void }) => event.preventDefault()
          : rest.onInteractOutside
      }
      {...rest}
    >
      {resolvedChildren}
    </ChakraMenu.Root>
  );
}

const Menu = MenuRoot as MenuComponent;
Menu.Button = MenuButton;
Menu.List = MenuList;
Menu.Item = MenuItem;
Menu.Label = MenuLabel;
Menu.Divider = MenuDivider;

export default Menu;
